import React from 'react'
import PropTypes from 'prop-types'
import {Box} from '@chakra-ui/react3'
import {isEqual} from 'lodash'
import Image from '../../components/cms/image'
import Video from '../../components/cms/video'
import {useStyles} from './context'
import {hasValidImageSrc, isVideoAsset} from '../../utils/utils'

/**
 * Hero asset component to display image or video
 *
 * @component
 * @param {object} [normalizedMobile] - Normalized mobile asset (editor-provided fields only)
 * @param {object} [normalizedDesktop] - Normalized desktop asset (editor-provided fields only)
 * @param {object|function} [videoRef] - Ref object or callback for the video element
 * @returns {JSX.Element|null} Rendered asset or null if no asset provided
 */
const HeroAsset = ({normalizedMobile, normalizedDesktop, mobileVideoRef, desktopVideoRef}) => {
    const styles = useStyles()

    if (!normalizedMobile && !normalizedDesktop) return null

    const renderAssetContent = (currentAsset, ref) => {
        if (!currentAsset) return null

        const {type: assetType, ...assetProps} = currentAsset

        if (isVideoAsset(currentAsset)) {
            return <Video ref={ref} {...currentAsset} loading="eager" />
        }

        // For image assets, check if src is valid
        if (!hasValidImageSrc(currentAsset)) return null

        return (
            <Image
                {...assetProps}
                widths={{base: '100vw', l: '1680px'}}
                loading="eager"
                objectFit="cover"
                preload
                // fetchpriority="high" optimizes LCP (Largest Contentful Paint) by prioritizing
                // hero image download over other resources. Combined with loading="eager" and
                // preload, this ensures fastest possible above-the-fold render.
                fetchpriority="high"
            />
        )
    }

    // If only one asset is provided or both are the same after normalization, render once
    if (!normalizedMobile || !normalizedDesktop || isEqual(normalizedMobile, normalizedDesktop)) {
        const singleAsset = normalizedMobile || normalizedDesktop
        return (
            <Box css={styles.assetContainer}>
                {renderAssetContent(singleAsset, mobileVideoRef)}
                {/* Darken Layer Left */}
                <Box css={styles.darkenLayerLeft} />
                {/* Darken Layer Bottom */}
                <Box css={styles.darkenLayerBottom} />
            </Box>
        )
    }

    // Different assets for mobile/desktop - render both with display toggles
    return (
        <Box css={styles.assetContainer}>
            {/* Mobile asset */}
            <Box display={{base: 'block', s: 'none'}} height="100%">
                {renderAssetContent(normalizedMobile, mobileVideoRef)}
            </Box>
            {/* Desktop asset */}
            <Box display={{base: 'none', s: 'block'}} height="100%">
                {renderAssetContent(normalizedDesktop, desktopVideoRef)}
            </Box>
            {/* Darken Layer Left */}
            <Box css={styles.darkenLayerLeft} />
            {/* Darken Layer Bottom */}
            <Box css={styles.darkenLayerBottom} />
        </Box>
    )
}

HeroAsset.propTypes = {
    normalizedMobile: PropTypes.object,
    normalizedDesktop: PropTypes.object,
    mobileVideoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    desktopVideoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
}

export default HeroAsset
